import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Container,
  Stack,
  CircularProgress,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

const EmailTheming = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const themesPerPage = 6;

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await postRequest(
          'private/email-themes',
          {
            page: currentPage,
            limit: themesPerPage,
            search: searchTerm,
            isActive: 'true'
          },
          authCtx.token
        );

        if (response.status === 200) {
          setThemes(response.data.themes);
          setTotalPages(response.data.pagination.totalPages);
          setTotalCount(response.data.pagination.totalCount);
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch email themes:', error);
        // Silently handle the error without showing popup
        setThemes([]);
        setTotalPages(1);
        setLoading(false);
      }
    };

    fetchThemes();
  }, [searchTerm, currentPage, authCtx.token, themesPerPage]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleAddNewTheme = () => {
    navigate('/communication/email-theming/create');
  };

  const handleEditTheme = (themeId) => {
    navigate(`/communication/email-theming/edit/${themeId}`);
  };

  // Remove local filtering since we're doing server-side filtering
  const filteredThemes = themes;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Email Theming', path: '' }
        ]}
      />

      {/* Header Section */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="400" color="text.primary">
            Email Theming
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNewTheme}
            sx={{
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#333',
                color: '#333'
              }
            }}
          >
            Add New Theme
          </Button>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Name
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search themes..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              sx={{
                borderColor: '#666',
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                  color: '#333'
                }
              }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Themes Grid */}
      <Grid container spacing={3}>
        {filteredThemes.map((theme) => (
          <Grid item xs={12} md={6} key={theme._id}>
            <Card
              sx={{
                height: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              {/* Theme Name Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Typography variant="h6" fontWeight="500" color="text.primary">
                  {theme.name}
                </Typography>
              </Box>

              {/* Email Preview */}
              <CardContent sx={{ p: 0, height: 400, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    backgroundColor: theme.backgroundColor,
                    position: 'relative'
                  }}
                >
                  {/* Preview Content */}
                  <Box sx={{ p: 3 }}>
                    {/* Theme Image or Type Text */}
                    {theme.themeImage ? (
                      <Box
                        sx={{
                          mb: 3,
                          textAlign: 'center',
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <img
                          src={theme.themeImage}
                          alt={theme.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '120px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{
                          color: theme.headerColor,
                          fontWeight: 'bold',
                          mb: 3,
                          textAlign: 'center'
                        }}
                      >
                        {theme.type}
                      </Typography>
                    )}

                    <Typography variant="body1" sx={{ mb: 2, color: theme.textColor }}>
                      {theme.greeting}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.headerColor,
                        fontWeight: 'bold',
                        mb: 2
                      }}
                    >
                      {theme.title}
                    </Typography>

                    <Typography variant="body1" sx={{ color: theme.textColor }}>
                      {theme.content}{' '}
                      <Typography
                        component="span"
                        sx={{
                          color: theme.linkColor,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        link
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Edit Button - positioned at bottom */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16
                    }}
                  >
                    <Button
                      variant="text"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditTheme(theme._id)}
                      sx={{
                        color: theme.headerColor,
                        '&:hover': {
                          backgroundColor: `${theme.headerColor}10`
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: '#dc2626' }} />
            <Typography variant="body1" color="text.secondary">
              Loading email themes...
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Empty State */}
      {filteredThemes.length === 0 && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#ffffff',
            borderRadius: 3,
            border: '1px solid #e5e7eb'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <AddIcon sx={{ fontSize: 40, color: '#dc2626' }} />
          </Box>
          <Typography variant="h5" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
            No themes found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            {searchTerm ? 'Try adjusting your search terms or create a new theme' : 'Get started by creating your first email theme to customize your communication'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddNewTheme}
            sx={{
              backgroundColor: '#7c3aed',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.3)',
              '&:hover': {
                backgroundColor: '#6d28d9',
                boxShadow: '0 8px 15px -3px rgba(124, 58, 237, 0.3)'
              }
            }}
          >
            Create Your First Theme
          </Button>
        </Box>
      )}

      {/* Pagination - Show only if more than 6 themes */}
      {totalCount > themesPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#dc2626',
                    '&:hover': {
                      backgroundColor: '#b91c1c'
                    }
                  }
                }
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Showing {((currentPage - 1) * themesPerPage) + 1}-{Math.min(currentPage * themesPerPage, totalCount)} of {totalCount} themes
            </Typography>
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default EmailTheming;